/**
 * Agent-facing TASK tools (RFC 0232 over RFC 0219).
 *
 * The model underneath already existed and is not duplicated here: a task is an
 * object entry on a session's log, stored and projected by `SessionTaskLog`.
 * What was missing was any way for an agent to reach it — the tool catalogue
 * shipped documents, sessions, comments, and apps, but nothing for the tasks
 * the same agent was being asked to track. So an agent kept its plan in its own
 * head (or in a scratch file) while the project had a real task store nobody
 * could write to.
 *
 * These are thin: parse arguments, call the namespace, render a compact text
 * result. All the lifecycle rules — session ownership, dependency unblocking,
 * source dedup — stay in `SessionTaskLog` where the CLI and the UI already get
 * them.
 *
 * Session-scoped, like `interactionTools`: a task created by a turn belongs to
 * that turn's session, which is what makes the session lens (`my_tasks`) a
 * projection rather than a stored set.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import type { ProjectDoc } from '../model/project/types'
import {
  isSessionDone,
  projectSessionStatus,
  registryStatus,
} from '../model/session/reads'
import { completionRule, mayComplete } from '../model/tasks/events'
import { nextUnownedTask } from '../model/tasks/goal'
import { SessionTaskLog } from '../model/tasks/mutations'
import type {
  Task,
  TaskCompletion,
  TaskEvent,
  TaskPriority,
  TaskRef,
  TaskStatus,
} from '../model/tasks/types'
import { projectWorldProjects } from '../model/world/reads'
import { registryHolding } from '../services/comments/registry-holding'
import { type ToolDef, worldListsProject } from './project-tools'

const STATUSES: readonly TaskStatus[] = [
  'pending',
  'active',
  'complete',
  'blocked',
  'cancelled',
]
const PRIORITIES: readonly TaskPriority[] = [
  'low',
  'normal',
  'high',
  'critical',
]

/** A short, stable handle for the model to pass back. Task ids are doc ids. */
function shortId(id: string): string {
  return id.slice(0, 8)
}

type TaskLineItem = Pick<
  Task,
  'id' | 'title' | 'status' | 'priority' | 'blockedBy'
>

/** One task as a single line: `abc12345  [active]  high  Title`. */
function taskLine(task: TaskLineItem): string {
  const priority =
    task.priority && task.priority !== 'normal' ? `  ${task.priority}` : ''
  const blocked =
    task.blockedBy.length > 0 ? `  (blocked by ${task.blockedBy.length})` : ''
  return `${shortId(task.id)}  [${task.status}]${priority}  ${task.title}${blocked}`
}

function renderList(tasks: TaskLineItem[]): string {
  if (tasks.length === 0) return '(no tasks)'
  return tasks.map(taskLine).join('\n')
}

/** The full record, for `read_task`. `sessions` is each session the task
 *  names, with what it is doing now (RFC 0319 3c). */
function renderTask(task: Task, sessions: string[] = []): string {
  const lines = [
    `id: ${task.id}`,
    `title: ${task.title}`,
    `status: ${task.status}`,
    ...(task.priority ? [`priority: ${task.priority}`] : []),
    ...(task.blockedBy.length > 0
      ? [`blockedBy: ${task.blockedBy.map(shortId).join(', ')}`]
      : []),
    ...(task.parentId ? [`parent: ${shortId(task.parentId)}`] : []),
    ...(task.subtaskIds.length > 0
      ? [`subtasks: ${task.subtaskIds.map(shortId).join(', ')}`]
      : []),
    ...(task.refs.length > 0
      ? ['refs:', ...task.refs.map((r) => `  ${refLine(r)}`)]
      : []),
    `completes on: ${completionRule(task)}`,
    ...((task.events ?? []).length > 0
      ? ['events:', ...(task.events ?? []).map((e) => `  ${eventLine(e)}`)]
      : []),
    ...(sessions.length > 0 ? ['sessions:', ...sessions] : []),
    `created: ${task.createdAt}`,
    `updated: ${task.updatedAt}`,
    ...(task.completedAt ? [`completed: ${task.completedAt}`] : []),
    ...(task.completionNote ? [`completionNote: ${task.completionNote}`] : []),
  ]
  if (task.description) lines.push('', task.description)
  return lines.join('\n')
}

/** `document 2kSh…  about`: what a ref is, its target, and why it is here. */
function refLine(ref: TaskRef): string {
  return [ref.type, ref.target, ref.role].filter(Boolean).join('  ')
}

/** `merged  2026-09-22T…  github:owner/repo#12  note` (RFC 0319 3g). */
function eventLine(event: TaskEvent): string {
  return [
    event.type,
    event.at,
    ...(event.refs ?? []).map((r) => r.target),
    event.note,
  ]
    .filter(Boolean)
    .join('  ')
}

function str(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

/** Refs as the model sends them: an array of `{ type, target, role }`, or the
 *  same as a JSON string. Anything without a type and a target is dropped. */
function refsArg(value: unknown): TaskRef[] {
  let raw = value
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw)
    } catch {
      return []
    }
  }
  if (!Array.isArray(raw)) return []
  const out: TaskRef[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const r = item as Record<string, unknown>
    const type = str(r.type)
    const target = str(r.target)
    const role = str(r.role)
    if (type && target) out.push({ type, target, ...(role ? { role } : {}) })
  }
  return out
}

const REFS_SCHEMA = {
  type: 'array',
  description:
    'What the task touches, each { type, target, role }. type: document, session, comment, pr, file, person, or an app type. target: the Mod id, or a URI such as github:owner/repo#12. role: about, closed-by, raised-by, worked-by.',
  items: {
    type: 'object',
    properties: {
      type: { type: 'string' },
      target: { type: 'string' },
      role: { type: 'string' },
    },
    required: ['type', 'target'],
  },
}

const COMPLETIONS: readonly TaskCompletion[] = ['manual', 'merge']

const COMPLETION_SCHEMA = {
  type: 'string',
  description:
    'What completes it (RFC 0319 3g). "merge": a slice of a build order, completed only when its branch merges; until then it stays active with the work recorded as an implemented event. "manual": someone ticks it. Left out, a task that names a branch or a PR is merge-driven and anything else is manual.',
}

export interface TaskToolOptions {
  /**
   * RFC 0209 share hook, forwarded to `SessionTaskLog`: a newly created task
   * content doc must be announced or peers see the id on the log and get
   * document-unavailable when they try to read it.
   */
  announce?: (documentIds: string[]) => void
  /** RFC 0336 S1: the World, so a task from another project is changed in
   *  the project that lists it rather than this session's. */
  worldId?: string | null
}

export function taskTools(
  repo: Repo,
  projectId: string,
  sessionId: string,
  options: TaskToolOptions = {},
): Record<string, ToolDef> {
  const log = new SessionTaskLog(repo as never, options.announce, projectId)

  /** This session, as the task it touches records it (RFC 0319 3c). */
  const workedBy = {
    type: 'session',
    target: sessionId,
    role: 'worked-by',
  } as const

  /**
   * One line per session the task names: its state now, why it is named, and
   * its title. The state is the session's own (`list_sessions` reads the same
   * one); the task never copies it, so it cannot go stale.
   */
  const sessionLines = async (task: Task): Promise<string[]> => {
    const named = new Map<string, string[]>()
    for (const r of task.refs) {
      if (r.type !== 'session') continue
      named.set(r.target, [...(named.get(r.target) ?? []), r.role ?? ''])
    }
    if (named.size === 0) return []
    const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
    const meta = project.doc()?.sessionMeta ?? {}
    return Promise.all(
      [...named].map(async ([id, roles]) => {
        let state = 'unknown'
        if (
          isSessionDone({
            ...meta[id],
            status: registryStatus(meta[id], Date.now()),
          })
        )
          state = 'done'
        else {
          try {
            const session = await repo.find(id as AnyDocumentId)
            await (session as { whenReady?: () => Promise<void> }).whenReady?.()
            state = projectSessionStatus(session.doc())
          } catch {
            // Unreachable is unknown, never idle: see list_sessions.
          }
        }
        const self = id === sessionId ? '  (this session)' : ''
        const why = roles.filter(Boolean).join(', ')
        return `  ${id}  ${state}  ${why}  ${meta[id]?.title ?? '(untitled)'}${self}`
      }),
    )
  }

  /**
   * The log for the project that lists a task (RFC 0336 S1). A task is its own
   * document, so reading it works from anywhere; changing it also updates the
   * owning project's `tasks` map, which has to be the right project.
   */
  const homeOf = (taskId: string): Promise<string> =>
    registryHolding(repo, projectId, options.worldId, { taskId })
  const logFor = async (taskId: string): Promise<SessionTaskLog> => {
    const home = await homeOf(taskId)
    return home === projectId
      ? log
      : new SessionTaskLog(repo as never, options.announce, home)
  }

  /** The projects the World lists, for a short id that is not this one's. */
  const worldProjectIds = async (): Promise<string[]> => {
    if (!options.worldId) return []
    try {
      const world = await repo.find(options.worldId as AnyDocumentId)
      await (world as { whenReady?: () => Promise<void> }).whenReady?.()
      return projectWorldProjects(world.doc() as never).map((p) => p.projectId)
    } catch {
      return []
    }
  }

  /** Resolve a model-supplied id, which is usually the 8-char short form. */
  const resolve = async (idOrPrefix: string): Promise<Task> => {
    const direct = await log.get(idOrPrefix)
    if (direct) return direct
    // Every project the World lists, not just this one: a task this session
    // created elsewhere is still named by its short id (RFC 0336 S1).
    const projects = [projectId, ...(await worldProjectIds())].filter(
      (id, i, all) => all.indexOf(id) === i,
    )
    const hits: { id: string }[] = []
    for (const pid of projects) {
      const all = await log
        .summaries(pid, { includeCompleted: true })
        .catch(() => [])
      for (const t of all)
        if (t.id.startsWith(idOrPrefix) && !hits.some((h) => h.id === t.id))
          hits.push(t)
    }
    if (hits.length === 1) {
      const task = await log.get(hits[0].id)
      if (task) return task
    }
    if (hits.length > 1) {
      throw new Error(
        `"${idOrPrefix}" matches ${hits.length} tasks; use the full id from read_task.`,
      )
    }
    throw new Error(`No task matches "${idOrPrefix}".`)
  }

  return {
    list_tasks: {
      description:
        'List tasks in this project. Use this before creating a task, so related work is updated rather than duplicated. Completed tasks are hidden unless includeCompleted is true.',
      inputSchema: {
        type: 'object',
        properties: {
          status: {
            type: 'string',
            description: `Filter by status (${STATUSES.join(' | ')}).`,
          },
          priority: {
            type: 'string',
            description: `Filter by priority (${PRIORITIES.join(' | ')}).`,
          },
          includeCompleted: {
            type: 'boolean',
            description: 'Include completed and cancelled tasks.',
          },
        },
      },
      execute: async (a) => {
        const status = str(a.status) as TaskStatus
        const priority = str(a.priority) as TaskPriority
        const tasks = await log.summaries(projectId, {
          ...(STATUSES.includes(status) ? { status } : {}),
          ...(PRIORITIES.includes(priority) ? { priority } : {}),
          ...(a.includeCompleted === true ? { includeCompleted: true } : {}),
        })
        return renderList(tasks)
      },
    },

    my_tasks: {
      description:
        'List the tasks that name THIS session: the ones it made and the ones it worked. It is the right lens for reporting your own progress.',
      inputSchema: {
        type: 'object',
        properties: {
          includeCompleted: {
            type: 'boolean',
            description: 'Include completed and cancelled tasks.',
          },
        },
      },
      execute: async (a) => {
        const tasks = await log.forSession(projectId, sessionId, {
          ...(a.includeCompleted === true ? { includeCompleted: true } : {}),
        })
        return renderList(tasks)
      },
    },

    read_task: {
      description:
        'Read one task in full: description, status, priority, dependencies, parent and subtasks, refs (what it is about, the sessions that worked it, what closed it), and completion note. Do this before acting on a task rather than working from its title alone.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description:
              'The task id (the short form from list_tasks is fine).',
          },
        },
        required: ['taskId'],
      },
      execute: async (a) => {
        const id = str(a.taskId ?? a.task_id ?? a.id)
        if (!id) throw new Error('taskId is required')
        const task = await resolve(id)
        return renderTask(task, await sessionLines(task))
      },
    },

    create_task: {
      description:
        "Create a pending task in this project (or the one projectId names), attached to the current session. Use it to record work that outlives this turn — a coarse outcome you are about to execute, or a follow-up you found and are not doing now. Set the task active with update_task when it becomes this session's current work. Do not create one for a step you are finishing inside this same turn.",
      inputSchema: {
        type: 'object',
        properties: {
          title: {
            type: 'string',
            description: 'One line describing the work.',
          },
          description: {
            type: 'string',
            description:
              'Optional detail: acceptance criteria, context, and what "done" means.',
          },
          priority: {
            type: 'string',
            description: `Optional priority (${PRIORITIES.join(' | ')}). Defaults to normal.`,
          },
          parentId: {
            type: 'string',
            description:
              'Make this a subtask of that task. The parent lists it in the same call.',
          },
          refs: REFS_SCHEMA,
          completion: COMPLETION_SCHEMA,
          projectId: {
            type: 'string',
            description:
              'The project the task belongs to, as a search result names it. Omit for this project. A subtask goes where its parent is.',
          },
        },
        required: ['title'],
      },
      execute: async (a) => {
        const title = str(a.title)
        if (!title) throw new Error('title is required')
        const description = str(a.description)
        const priority = str(a.priority) as TaskPriority
        const parentKey = str(a.parentId ?? a.parent_id)
        const parent = parentKey ? await resolve(parentKey) : null
        const completion = str(a.completion) as TaskCompletion
        // RFC 0336 S5: creating is where a project is named. A subtask lives
        // with its parent; otherwise the project asked for, else this one.
        const asked = str(a.projectId)
        if (
          !parent &&
          asked &&
          asked !== projectId &&
          !(await worldListsProject(repo, options.worldId, asked))
        ) {
          throw new Error(`${asked} is not a project in this World`)
        }
        const where = parent
          ? await logFor(parent.id)
          : asked && asked !== projectId
            ? new SessionTaskLog(repo as never, options.announce, asked)
            : log
        const task = await where.create(sessionId, {
          title,
          ...(description ? { description } : {}),
          ...(PRIORITIES.includes(priority) ? { priority } : {}),
          ...(parent ? { parentId: parent.id } : {}),
          ...(COMPLETIONS.includes(completion) ? { completion } : {}),
          refs: refsArg(a.refs),
        })
        return parent
          ? `Created ${task.id}  ${task.title}  (subtask of ${shortId(parent.id)})`
          : `Created ${task.id}  ${task.title}`
      },
    },

    update_task: {
      description:
        'Update a task: its status, priority, title, description, parent, or refs. Set status to "active" when you take it as this session\'s current goal; this also attaches the session as a worker. Keep the status honest — a blocked task stays blocked, with the blocker written into the description. A task that completes on merge stays active until its branch merges, whatever status you ask for. Updating a task records this session as having worked it.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The task id (the short form is fine).',
          },
          status: {
            type: 'string',
            description: `New status (${STATUSES.join(' | ')}).`,
          },
          priority: {
            type: 'string',
            description: `New priority (${PRIORITIES.join(' | ')}).`,
          },
          title: { type: 'string', description: 'New title.' },
          description: { type: 'string', description: 'New description.' },
          parentId: {
            anyOf: [{ type: 'string' }, { type: 'null' }],
            description:
              'Move under this parent task. Pass null to make it a root task.',
          },
          refs: {
            ...REFS_SCHEMA,
            description: `Refs to ADD. ${REFS_SCHEMA.description}`,
          },
          removeRefs: {
            ...REFS_SCHEMA,
            description:
              'Refs to REMOVE, matched on type, target and role. For a link ' +
              'that was wrong, e.g. a plan the task is listed in but not about.',
          },
          completion: COMPLETION_SCHEMA,
        },
        required: ['taskId'],
      },
      execute: async (a) => {
        const id = str(a.taskId ?? a.task_id ?? a.id)
        if (!id) throw new Error('taskId is required')
        const task = await resolve(id)
        const status = str(a.status) as TaskStatus
        const priority = str(a.priority) as TaskPriority
        if (str(a.status) && !STATUSES.includes(status)) {
          throw new Error(
            `"${a.status}" is not a task status. Use one of: ${STATUSES.join(', ')}.`,
          )
        }
        if (str(a.priority) && !PRIORITIES.includes(priority)) {
          throw new Error(
            `"${a.priority}" is not a task priority. Use one of: ${PRIORITIES.join(', ')}.`,
          )
        }
        const title = str(a.title)
        const description = str(a.description)
        const completion = str(a.completion) as TaskCompletion
        const movesParent =
          Object.hasOwn(a, 'parentId') || Object.hasOwn(a, 'parent_id')
        const parentKey = str(a.parentId ?? a.parent_id)
        const parent = parentKey ? await resolve(parentKey) : null
        const updated = await (await logFor(task.id)).update(task.id, {
          ...(STATUSES.includes(status) ? { status } : {}),
          ...(PRIORITIES.includes(priority) ? { priority } : {}),
          ...(title ? { title } : {}),
          ...(description ? { description } : {}),
          ...(movesParent ? { parentId: parent?.id ?? null } : {}),
          ...(COMPLETIONS.includes(completion) ? { completion } : {}),
          addRefs: [workedBy, ...refsArg(a.refs)],
          removeRefs: refsArg(a.removeRefs ?? a.remove_refs),
        })
        const line = `Updated ${shortId(updated.id)}  [${updated.status}]  ${updated.title}`
        if (status !== 'complete' || updated.status === 'complete') return line
        // Say so rather than let the caller assume the status it asked for
        // (RFC 0319 3g).
        const gate = mayComplete(updated)
        return gate.allowed ? line : `${line}\nNot completed: ${gate.reason}`
      },
    },

    record_task_event: {
      description:
        'Record something that happened to a task, without moving its status (RFC 0319 3g): an implementation commit, a review verdict, a merge, a question, or an app\'s own event. Status is a claim; an event is the evidence behind it. Use "merged" with a closed-by PR ref when a branch lands, which is what completes a merge-driven slice.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The task id (the short form is fine).',
          },
          type: {
            type: 'string',
            description:
              'What happened: implemented, reviewed, merged, asked, answered, imported, or your own name for it.',
          },
          note: { type: 'string', description: 'One line saying what it was.' },
          refs: {
            ...REFS_SCHEMA,
            description: `What the event is evidence of. ${REFS_SCHEMA.description}`,
          },
        },
        required: ['taskId', 'type'],
      },
      execute: async (a) => {
        const id = str(a.taskId ?? a.task_id ?? a.id)
        if (!id) throw new Error('taskId is required')
        const type = str(a.type)
        if (!type) throw new Error('type is required')
        const task = await resolve(id)
        const updated = await (await logFor(task.id)).recordEvent(task.id, {
          type,
          at: new Date().toISOString(),
          by: sessionId,
          ...(str(a.note) ? { note: str(a.note) } : {}),
          refs: refsArg(a.refs),
        })
        return `Recorded ${type} on ${shortId(updated.id)}  [${updated.status}]  ${updated.title}`
      },
    },

    complete_task: {
      description:
        'Mark a task complete, with a note saying what changed and how it was verified. Completing a task also unblocks anything waiting on it, which is reported back to you. Only complete a task when its criteria are met and the person is clear on the outcome, not merely because the artifact exists. A task that completes on merge (RFC 0319 3g) does NOT complete here without a closed-by ref for the PR: it records the work as an implemented event and stays active, and you are told so.',
      inputSchema: {
        type: 'object',
        properties: {
          taskId: {
            type: 'string',
            description: 'The task id (the short form is fine).',
          },
          note: {
            type: 'string',
            description:
              'What was done, what validation ran, and any remaining risk.',
          },
          refs: {
            ...REFS_SCHEMA,
            description:
              'What closed it, e.g. [{ "type": "pr", "target": "github:owner/repo#12", "role": "closed-by" }].',
          },
        },
        required: ['taskId'],
      },
      execute: async (a) => {
        const id = str(a.taskId ?? a.task_id ?? a.id)
        if (!id) throw new Error('taskId is required')
        const task = await resolve(id)
        const note = str(a.note)
        // The task's own project: completing writes its registry entry and
        // scans for what it unblocks, both of which live where it does.
        const home = await homeOf(task.id)
        const result = await (await logFor(task.id)).complete(
          home,
          task.id,
          note || undefined,
          [workedBy, ...refsArg(a.refs)],
        )
        if (result.deferred) {
          return `Not completed: ${shortId(result.task.id)}  [${result.task.status}]  ${result.task.title}\n${result.deferred.reason}\nThe work is recorded as an implemented event.`
        }
        const unblocked = result.unblockedTasks
        const completedLine =
          unblocked.length === 0
            ? `Completed ${shortId(result.task.id)}  ${result.task.title}`
            : `Completed ${shortId(result.task.id)}  ${result.task.title}\nUnblocked:\n${renderList(unblocked)}`
        const next = nextUnownedTask(
          result.task,
          await log.summaries(projectId),
        )
        return next
          ? `${completedLine}\nNext unowned task: ${taskLine(next)}\nOffer to start this task with one recommended choice. If accepted, start one session for it with start_session and its taskId; that refuses a task another worker has taken.`
          : `${completedLine}\nNo unowned task remains. Ask what is on the person's mind, with your suggested starting point, then turn the answer into a short ordered task list with reasons.`
      },
    },
  }
}
